<template>
  <div class="mobile-video-create" :class="{ 'dark-theme': isDark }">
    <!-- 顶部导航 -->
    <header class="mobile-header">
      <button @click="goBack" class="back-btn">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div class="header-center">
        <h1>AI视频生成</h1>
        <div v-if="userQuota" class="quota-info">
          <i class="fas fa-bolt"></i>
          <span>{{ userQuota.todayCalls }}/{{ userQuota.dailyLimit }}</span>
        </div>
      </div>
      <div class="header-actions">
        <!-- 主题切换按钮 -->
        <div class="theme-toggle" @click="toggleTheme">
          <i :class="isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
        </div>
      </div>
    </header>

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 步骤指示器 -->
      <div class="step-indicator">
        <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <span>选择模板</span>
        </div>
        <div class="step-line"></div>
        <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
          <div class="step-number">2</div>
          <span>设置创意</span>
        </div>
        <div class="step-line"></div>
        <div class="step" :class="{ active: currentStep >= 3 }">
          <div class="step-number">3</div>
          <span>生成创意</span>
        </div>
      </div>

      <!-- 步骤1：模板选择 -->
      <div v-if="currentStep === 1" class="step-content template-selection">
        <div class="section-header">
          <h2>选择视频模板</h2>
          <p>选择适合您需求的AI视频模板</p>
        </div>

        <!-- 搜索栏 -->
        <div class="search-bar">
          <i class="fas fa-search"></i>
          <input 
            v-model="searchKeyword" 
            type="text" 
            placeholder="搜索模板..."
            @input="filterTemplates"
          />
        </div>

        <!-- 分类标签 -->
        <div class="category-tabs">
          <button 
            v-for="category in categories" 
            :key="category.key"
            @click="selectCategory(category.key)"
            :class="{ active: selectedCategory === category.key }"
            class="category-tab"
          >
            {{ category.name }}
          </button>
        </div>

        <!-- 模板网格 -->
        <div class="templates-grid">
          <div 
            v-for="template in filteredTemplates" 
            :key="template.key"
            @click="selectTemplate(template)"
            :class="{ selected: selectedTemplateKey === template.key }"
            class="template-card"
          >
            <div class="template-image">
              <img :src="template.image" :alt="template.name" loading="lazy" />
              <div class="template-overlay">
                <i class="fas fa-play"></i>
              </div>
            </div>
            <div class="template-info">
              <h3>{{ template.name }}</h3>
              <p>{{ template.description }}</p>
              <span v-if="template.tag" class="template-tag">{{ template.tag }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 步骤2：参数设置 -->
      <div v-if="currentStep === 2" class="step-content parameter-setting">
        <div class="section-header">
          <h2>设置生成参数</h2>
          <p>根据您的需求调整参数</p>
        </div>

        <!-- 选中的模板信息 -->
        <div v-if="currentTemplate" class="selected-template-info">
          <img :src="currentTemplate.image" :alt="currentTemplate.name" />
          <div class="template-details">
            <h3>{{ currentTemplate.name }}</h3>
            <p>{{ currentTemplate.description }}</p>
          </div>
        </div>

        <!-- 动态参数表单 -->
        <div v-if="currentTemplateParams.length > 0" class="parameter-form">
          <div 
            v-for="param in currentTemplateParams" 
            :key="param.name" 
            class="param-group"
          >
            <label class="param-label">
              {{ param.label || param.name }}
              <span v-if="param.required" class="required">*</span>
            </label>
            
            <!-- 1. 优先判断：URL/文件上传参数（包括data_type为text/string但名称表明是URL的） -->
            <template v-if="isUrlLikeParam(param)">
              <div class="url-input-group">
                <input
                  v-model="templateParams[param.input_name || param.name]"
                  type="url"
                  :placeholder="`点击按钮上传${param.label || param.name}`"
                  class="form-input"
                  readonly
                />
                <button 
                  type="button" 
                  class="upload-btn"
                  @click="handleFileUpload(param.input_name || param.name, param)"
                  :title="determineParamType(param) === 'image' ? '选择图片或拍照' : '选择视频或录制'"
                >
                  <i :class="determineParamType(param) === 'image' ? 'fas fa-images' : 'fas fa-video'"></i>
                  {{ determineParamType(param) === 'image' ? '选择图片' : '选择视频' }}
                </button>
              </div>
              <!-- 文件预览 -->
              <div v-if="templateParams[param.input_name || param.name] && (templateParams[param.input_name || param.name].startsWith('blob:') || templateParams[param.input_name || param.name].startsWith('http'))" class="file-preview">
                <div class="preview-item">
                  <!-- 顶部操作栏 -->
                  <div class="preview-header">
                    <!-- 状态指示器 -->
                    <div class="preview-status">
                      <div 
                        v-if="templateParams[param.input_name || param.name].startsWith('http')" 
                        class="status-badge status-uploaded"
                        title="已上传到服务器"
                      >
                        <i class="fas fa-check"></i>
                        <span>已上传</span>
                      </div>
                      <div 
                        v-else 
                        class="status-badge status-local"
                        title="仅本地预览，未上传到服务器"
                      >
                        <i class="fas fa-exclamation-circle"></i>
                        <span>本地预览</span>
                      </div>
                    </div>
                    <!-- 删除按钮 -->
                    <button 
                      type="button" 
                      class="remove-btn"
                      @click="templateParams[param.input_name || param.name] = ''"
                      title="移除文件"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                  
                  <!-- 文件内容预览 -->
                  <div class="preview-content">
                    <img 
                      v-if="determineParamType(param) === 'image'" 
                      :src="templateParams[param.input_name || param.name]" 
                      alt="预览"
                      class="preview-image"
                    />
                    <video 
                      v-else-if="determineParamType(param) === 'video'"
                      :src="templateParams[param.input_name || param.name]" 
                      controls
                      class="preview-video"
                    ></video>
                  </div>
                  
                  <!-- 文件信息 -->
                  <div class="preview-footer">
                    <div class="file-info">
                      <i :class="determineParamType(param) === 'image' ? 'fas fa-image' : 'fas fa-video'"></i>
                      <span class="file-type">{{ determineParamType(param) === 'image' ? '图片文件' : '视频文件' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- 2. 数字输入 -->
            <template v-else-if="param.data_type === 'int' || param.data_type === 'number' || param.data_type === 'float'">
              <input
                v-model.number="templateParams[param.input_name || param.name]"
                type="number"
                :min="param.min"
                :max="param.max"
                :step="param.data_type === 'float' ? 0.1 : 1"
                :placeholder="param.placeholder"
                class="form-input"
              />
            </template>

            <!-- 3. 布尔选择 -->
            <template v-else-if="param.data_type === 'bool' || param.data_type === 'boolean'">
              <label class="checkbox-label">
                <input
                  v-model="templateParams[param.input_name || param.name]"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span class="checkbox-text">{{ param.description || '启用此选项' }}</span>
              </label>
            </template>

            <!-- 4. 选择框 (原有的options或逗号分隔的选项) -->
            <template v-else-if="(param.options && param.options.length > 0) || hasCommaOptions(param)">
              <!-- 逗号分隔的选项使用按钮组 -->
              <template v-if="hasCommaOptions(param)">
                <div class="option-buttons">
                  <button 
                    v-for="option in getCommaOptions(param)" 
                    :key="option" 
                    @click="templateParams[param.input_name || param.name] = option"
                    :class="['option-btn', { 'active': templateParams[param.input_name || param.name] === option }]"
                    type="button"
                  >
                    {{ option }}
                  </button>
                </div>
              </template>
              <!-- 原有的options仍使用下拉框 -->
              <template v-else-if="param.options && param.options.length > 0">
                <select v-model="templateParams[param.input_name || param.name]" class="form-select">
                  <option value="">请选择</option>
                  <option v-for="option in param.options" :key="option.value" :value="option.value">
                    {{ option.label || option.value }}
                  </option>
                </select>
              </template>
            </template>

            <!-- 5. 文本输入（data_type为text/string且不是URL相关） -->
            <template v-else-if="param.data_type === 'text' || param.data_type === 'string'">
              <textarea
                v-if="param.multiline || (param.label && param.label.includes('描述')) || (param.name && param.name.includes('prompt'))"
                v-model="templateParams[param.input_name || param.name]"
                :placeholder="param.placeholder || `请输入${param.label || param.name}`"
                :maxlength="param.max_length || 1000"
                class="form-textarea"
                rows="4"
              ></textarea>
              <input
                v-else
                v-model="templateParams[param.input_name || param.name]"
                type="text"
                :placeholder="param.placeholder || `请输入${param.label || param.name}`"
                :maxlength="param.max_length || 500"
                class="form-input"
              />
            </template>

            <!-- 6. 默认文本输入 -->
            <template v-else>
              <input
                v-model="templateParams[param.input_name || param.name]"
                type="text"
                :placeholder="param.placeholder || `请输入${param.label || param.name}`"
                class="form-input"
              />
            </template>

            <!-- 参数描述 -->
            <div v-if="param.description" class="param-description">
              {{ param.description }}
            </div>
          </div>
        </div>
        
        <!-- 无参数提示 -->
        <div v-else class="no-params-tip">
          <i class="fas fa-info-circle"></i>
          <p>该模板无需设置额外参数，可直接生成视频</p>
        </div>

        <!-- 操作按钮 -->
        <div class="step-actions">
          <button @click="prevStep" class="prev-btn">
            <i class="fas fa-arrow-left"></i>
            上一步
          </button>
          <button 
            v-if="!isGenerating && !generatedVideo"
            @click="nextStep" 
            :disabled="!isFormValid"
            class="next-btn"
          >
            下一步
            <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>

      <!-- 步骤3：生成视频 -->
      <div v-if="currentStep === 3" class="step-content video-generation">
        <div class="section-header">
          <h2>生成视频</h2>
          <p>AI正在为您生成创意视频</p>
        </div>

        <!-- 生成参数预览 -->
        <div class="generation-preview">
          <div class="preview-header">
            <i class="fas fa-eye"></i>
            <h4>参数预览</h4>
          </div>
          
          <div class="preview-content">
            <div class="preview-item template-item">
              <div class="item-icon">
                <i class="fas fa-film"></i>
              </div>
              <div class="item-content">
                <span class="label">模板</span>
                <span class="value">{{ currentTemplate?.name }}</span>
              </div>
            </div>
            
            <div 
              v-for="param in currentTemplateParams.filter((p: { name: string; input_name?: string }) => templateParams[p.input_name || p.name] !== '' && templateParams[p.input_name || p.name] !== null && templateParams[p.input_name || p.name] !== undefined)" 
              :key="param.name" 
              class="preview-item"
            >
              <div class="item-icon">
                <i :class="getParamIcon(param)"></i>
              </div>
              <div class="item-content">
                <span class="label">{{ param.label || param.name }}</span>
                <span class="value" :class="{ 'url-value': isUrlParam(param) }">
                  <template v-if="param.data_type === 'bool' || param.data_type === 'boolean'">
                    <span class="bool-badge" :class="templateParams[param.input_name || param.name] ? 'bool-true' : 'bool-false'">
                      <i :class="templateParams[param.input_name || param.name] ? 'fas fa-check' : 'fas fa-times'"></i>
                      {{ templateParams[param.input_name || param.name] ? '是' : '否' }}
                    </span>
                  </template>
                  <template v-else-if="isUrlParam(param)">
                    <!-- 图片预览 -->
                    <div v-if="determineParamType(param) === 'image'" class="image-preview">
                      <img 
                        :src="templateParams[param.input_name || param.name]" 
                        :alt="param.label || param.name"
                        class="preview-img"
                        @error="handleImageError"
                      />
                      <div class="image-actions">
                        <button 
                          @click="copyToClipboard(templateParams[param.input_name || param.name])"
                          class="copy-url-btn"
                          title="复制图片链接"
                        >
                          <i class="fas fa-copy"></i>
                        </button>
                        <button 
                          @click="openImageInNewTab(templateParams[param.input_name || param.name])"
                          class="open-image-btn"
                          title="新窗口查看"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </button>
                      </div>
                    </div>
                    
                    <!-- 视频或其他文件URL -->
                    <span v-else class="url-display">
                      <span class="url-text">{{ getDisplayUrl(templateParams[param.input_name || param.name]) }}</span>
                      <button 
                        v-if="templateParams[param.input_name || param.name]" 
                        @click="copyToClipboard(templateParams[param.input_name || param.name])"
                        class="copy-url-btn"
                        title="复制链接"
                      >
                        <i class="fas fa-copy"></i>
                      </button>
                    </span>
                  </template>
                  <template v-else-if="typeof templateParams[param.input_name || param.name] === 'string' && templateParams[param.input_name || param.name].length > 30">
                    <span class="long-text" :title="templateParams[param.input_name || param.name]">
                      {{ templateParams[param.input_name || param.name].substring(0, 30) }}...
                    </span>
                  </template>
                  <template v-else>
                    {{ templateParams[param.input_name || param.name] }}
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 生成状态 -->
        <div class="generation-status">
          <transition name="fade-slide" mode="out-in">
            <div v-if="!isGenerating && !generatedVideo" key="ready" class="generation-ready">
              <div class="ready-content">
                <h3>准备就绪</h3>
                <p>点击火箭启动AI视频创作之旅</p>
                
                <!-- 火箭按钮 -->
                <div class="rocket-launch-container">
                  <button 
                    @click="handleRocketLaunch" 
                    class="rocket-btn"
                    :class="{ 'launching': isLaunching }"
                    :disabled="isLaunching"
                  >
                    <i class="fas fa-rocket rocket-icon" :class="{ 'fly-away': isLaunching }"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-else-if="isGenerating" key="progress" class="generation-progress">
              <div class="progress-icon">
                <i class="fas fa-magic fa-spin"></i>
              </div>
              <h3>AI正在创作中...</h3>
              
              <!-- 简化的时间信息 -->
              <div class="simple-time-info">
                <div class="time-item">
                  <span>已用时: {{ elapsedSeconds }} 秒</span>
                </div>
                <div v-if="predictedDuration" class="time-item">
                  <span>预计总时长: {{ Math.round(predictedDuration) }} 秒（历史估算）</span>
                </div>
              </div>
              
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: generationProgress + '%' }"></div>
              </div>
              <p class="progress-text">{{ generationStatus }}</p>
            </div>

            <div v-else-if="generatedVideo" key="complete" class="generation-complete">
              <div class="video-result">
                <h3>视频生成完成！</h3>
                <p>您的AI创意视频已生成完毕</p>
                
                <video 
                  v-if="generatedVideo.url"
                  :src="generatedVideo.url" 
                  :poster="generatedVideo.thumbnail"
                  controls 
                  preload="metadata"
                  class="result-video"
                  @loadstart="handleVideoLoadStart"
                  @loadedmetadata="handleVideoLoadedMetadata"
                  @error="handleVideoError"
                  @canplay="handleVideoCanPlay"
                >
                  您的浏览器不支持视频播放
                </video>
                
                <div v-if="!generatedVideo.url" class="video-error">
                  <i class="fas fa-exclamation-triangle"></i>
                  <p>视频地址获取失败，请重试生成</p>
                </div>
                
                <div class="video-actions">
                  <button v-if="generatedVideo.url" @click="copyVideoUrl" class="copy-btn">
                    <i class="fas fa-link"></i>
                    复制链接
                  </button>
                  <button 
                    @click="downloadVideo" 
                    class="download-btn"
                    :disabled="isDownloading"
                    :class="{ 'downloading': isDownloading }"
                  >
                    <i :class="isDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                    {{ isDownloading ? '下载中...' : '下载视频' }}
                  </button>
                  <button v-if="generatedVideo.url" @click="reloadVideo" class="reload-btn full-width">
                    <i class="fas fa-redo"></i>
                    重新加载
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- 操作按钮 -->
        <div class="step-actions">
          <button v-if="!isGenerating" @click="prevStep" class="prev-btn">
            <i class="fas fa-arrow-left"></i>
            上一步
          </button>
          <button 
            v-if="generatedVideo"
            @click="createNew" 
            class="new-btn"
          >
            <i class="fas fa-plus"></i>
            创建新视频
          </button>
        </div>
      </div>

      <!-- 全局视频结果显示区域 - 不受步骤限制 -->
      <div v-if="generatedVideo && currentStep !== 3" class="global-video-result">
        <div class="video-result-card">
          <div class="card-header">
            <h3>
              <i class="fas fa-video"></i>
              生成完成的视频
            </h3>
            <button @click="backToVideoStep" class="back-to-video-btn">
              <i class="fas fa-eye"></i>
              查看详情
            </button>
          </div>
          
          <div class="video-preview">
            <video 
              :src="generatedVideo.url" 
              :poster="generatedVideo.thumbnail"
              controls 
              preload="metadata"
              class="preview-video"
              @error="handleVideoError"
            >
              您的浏览器不支持视频播放
            </video>
            
            <div class="video-info">
              <p class="video-title">{{ generatedVideo.title }}</p>
              <div class="video-actions-mini">
                <button @click="copyVideoUrl" class="mini-btn" title="复制视频链接">
                  <i class="fas fa-link"></i>
                </button>
                <button 
                  @click="downloadVideo" 
                  class="mini-btn" 
                  :title="isDownloading ? '下载中...' : '下载视频'"
                  :disabled="isDownloading"
                >
                  <i :class="isDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 模板预览弹窗 -->
    <div v-if="showTemplatePreview && previewTemplate" class="modal-overlay template-preview-overlay" @click="cancelTemplatePreview">
      <div class="modal-content template-preview-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ previewTemplate.name }}</h3>
          <button @click="cancelTemplatePreview" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body template-preview-body">
          <!-- 模板封面/样例视频 -->
          <div class="template-media">
            <div v-if="previewTemplate.video_url || previewTemplate.sample_video" class="sample-video">
              <video 
                :src="previewTemplate.video_url || previewTemplate.sample_video" 
                :poster="previewTemplate.image || previewTemplate.thumbnail"
                controls 
                preload="metadata"
                class="preview-video"
              >
                您的浏览器不支持视频播放
              </video>
            </div>
            <div v-else class="template-image">
              <img :src="previewTemplate.image || previewTemplate.thumbnail" :alt="previewTemplate.name" />
            </div>
          </div>
          
          <!-- 模板信息 -->
          <div class="template-info">
            <div class="template-description">
              <h4>模板介绍</h4>
              <p>{{ previewTemplate.description || '精美的AI视频模板，适合各种场景使用' }}</p>
            </div>
            
            <div v-if="previewTemplate.tags && previewTemplate.tags.length > 0" class="template-tags">
              <h4>标签</h4>
              <div class="tags-list">
                <span v-for="tag in previewTemplate.tags" :key="tag" class="tag">{{ tag }}</span>
              </div>
            </div>
            
            <div v-if="previewTemplate.parameters && previewTemplate.parameters.length > 0" class="template-params-info">
              <h4>参数设置</h4>
              <p>该模板需要设置 {{ previewTemplate.parameters.length }} 个参数</p>
              <div class="params-preview">
                <div v-for="param in previewTemplate.parameters.slice(0, 3)" :key="param.name" class="param-item">
                  <i class="fas fa-cog"></i>
                  <span>{{ param.label || param.name }}</span>
                  <span v-if="param.required" class="required-mark">*</span>
                </div>
                <div v-if="previewTemplate.parameters.length > 3" class="more-params">
                  <i class="fas fa-ellipsis-h"></i>
                  <span>还有 {{ previewTemplate.parameters.length - 3 }} 个参数...</span>
                </div>
              </div>
            </div>
            
            <div v-if="previewTemplate.estimated_duration" class="template-duration">
              <h4>预估时长</h4>
              <p>
                <i class="fas fa-clock"></i>
                {{ previewTemplate.estimated_duration }} 分钟
              </p>
            </div>
          </div>
        </div>
        
        <div class="modal-footer template-preview-footer">
          <button @click="cancelTemplatePreview" class="cancel-btn">
            <i class="fas fa-times"></i>
            取消
          </button>
          <button @click="confirmTemplate" class="confirm-btn">
            <i class="fas fa-check"></i>
            使用此模板
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from '@/utils/router.js';
import { useUserStoreHook } from '@/store/modules/user';
import { ElMessage } from '@/utils/message.js';
import ActionAPI from '@/api/comfyui-action';
import type { ActionUserMembership, ActionUserPackage } from '@/api/comfyui-action/types';
import { uploadFiles } from '@/api/file';
import { useTheme } from '@/composables/useTheme';

const router = useRouter();
const userStore = useUserStoreHook();

// 主题管理
const { isDark, loadTheme, toggleTheme } = useTheme();

// 状态管理
const currentStep = ref(1);
const selectedTemplateKey = ref('');
const selectedTemplate = ref<any>(null);
const searchKeyword = ref('');
const selectedCategory = ref('all');
const showAdvanced = ref(false);
const isGenerating = ref(false);
const isLaunching = ref(false);
const isDownloading = ref(false);
const generationProgress = ref(0);
const generationStatus = ref('');
const estimatedTime = ref(3);
const generatedVideo = ref<any>(null);
// 模板预览弹窗状态
const showTemplatePreview = ref(false);
const previewTemplate = ref<any>(null);

// 新增：时间统计和历史预估
const startTimestamp = ref<number | null>(null);
const elapsedSeconds = ref(0);
const elapsedTimer = ref<NodeJS.Timeout | null>(null);
const predictedDuration = ref<number | null>(null);

// 新增：进度详情
const progressDetail = ref({
  stage: '',
  message: '',
  currentStep: 0,
  totalSteps: 0
});


// 用户配额信息
const userQuota = ref<any>(null);

// 模板参数（动态根据选中模板生成）
const templateParams = ref<Record<string, any>>({});


// 模板数据
const templates = ref<any[]>([]);

// 分类选项
const categories = ref([
  { key: 'all', name: '全部' }
]);

// 判断是否为URL相关参数（完全与PC端一致）
const isUrlLikeParam = (param: any): boolean => {
  if (!param) return false;
  const dt = String(param.data_type || '').toLowerCase();
  const nm = String(param.name || '').toLowerCase();
  const inputName = String(param.input_name || '').toLowerCase();
  const label = String(param.label || '').toLowerCase();
  
  // 第一优先级：data_type 明确包含媒体类型
  if (dt.includes('image') || dt.includes('video') || dt.includes('file') || dt.includes('url')) {
    return true;
  }
  
  // 第二优先级：参数名明确包含媒体相关关键词（检查name和input_name）
  const mediaKeywords = /(image|img|pic|photo|picture|video|vid|mp4|mov|avi|url|file|upload)/;
  if (mediaKeywords.test(nm) || mediaKeywords.test(inputName)) {
    return true;
  }
  
  // 第三优先级：标签包含媒体相关关键词
  if (mediaKeywords.test(label)) {
    return true;
  }
  
  // 第四优先级：对于data_type为text/string的参数，检查参数名是否为URL格式
  if ((dt === 'text' || dt === 'string' || dt === 'str') && 
      ((nm.includes('_url') || nm.endsWith('url') || nm.includes('_link') || nm.endsWith('link')) ||
       (inputName.includes('_url') || inputName.endsWith('url') || inputName.includes('_link') || inputName.endsWith('link')))) {
    return true;
  }
  
  return false;
};

// 判断参数类型（图片还是视频）
const determineParamType = (param: any): string => {
  const fieldName = String(param.name || '').toLowerCase();
  const inputName = String(param.input_name || '').toLowerCase();
  const label = String(param.label || '').toLowerCase();
  
  // 基于标签匹配
  const labelImageMatch = /(图片|图像|照片|封面|缩略图|头像|poster|画面|截图|image|img|pic|photo)/.test(label) && 
                         !/(视频|影片|电影|短片|录像|video|vid|mp4|mov)/.test(label);
  const labelVideoMatch = /(视频|影片|电影|短片|录像|video|vid|mp4|mov|film|movie)/.test(label) && 
                         !/(图片|图像|照片|封面|缩略图|头像|poster|画面|截图|image|img|pic|photo)/.test(label);
  
  if (labelImageMatch) return 'image';
  if (labelVideoMatch) return 'video';
  
  // 基于字段名匹配
  const fieldImageMatch = ((fieldName.includes('image') || fieldName.includes('img') || fieldName.includes('pic') || fieldName.includes('photo')) &&
                          !fieldName.includes('video')) ||
                         ((inputName.includes('image') || inputName.includes('img') || inputName.includes('pic') || inputName.includes('photo')) &&
                          !inputName.includes('video'));
  const fieldVideoMatch = ((fieldName.includes('video') || fieldName.includes('vid') || fieldName.includes('mp4') || fieldName.includes('mov')) &&
                          !fieldName.includes('image') && !fieldName.includes('img')) ||
                         ((inputName.includes('video') || inputName.includes('vid') || inputName.includes('mp4') || inputName.includes('mov')) &&
                          !inputName.includes('image') && !inputName.includes('img'));
  
  if (fieldImageMatch) return 'image';
  if (fieldVideoMatch) return 'video';
  
  // 默认返回image
  return 'image';
};

// 检查参数是否有逗号分隔的选项
const hasCommaOptions = (param: any): boolean => {
  // 检查 default 或 example 字段是否包含逗号分隔的选项
  const defaultValue = param.default || param.example || '';
  return typeof defaultValue === 'string' && 
         defaultValue.includes('，') && // 中文逗号
         defaultValue.trim().length > 0 &&
         param.data_type === 'text';
};

// 获取逗号分隔的选项数组
const getCommaOptions = (param: any): string[] => {
  const defaultValue = param.default || param.example || '';
  if (typeof defaultValue === 'string' && defaultValue.includes('，')) {
    return defaultValue.split('，').map((item: string) => item.trim()).filter((item: string) => item.length > 0);
  }
  return [];
};

// 获取参数的键名（优先使用 input_name，如果没有则使用 name）
const getParamKey = (param: any): string => {
  return param.input_name || param.name || '';
};

// 文件上传处理
const handleFileUpload = async (paramName: string, param: any) => {
  const paramType = determineParamType(param);
  
  // 创建文件输入元素
  const input = document.createElement('input');
  input.type = 'file';
  
  // 根据参数类型设置接受的文件类型
  // 移除 capture 属性，让用户可以选择拍照或从相册选择
  if (paramType === 'image') {
    input.accept = 'image/*';
  } else if (paramType === 'video') {
    input.accept = 'video/*';
  } else {
    input.accept = 'image/*,video/*';
  }
  
  input.style.display = 'none';
  
  input.onchange = async (event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      console.log('选择的文件:', file.name, '类型:', file.type, '大小:', file.size);
      
      // 验证文件类型
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (paramType === 'image' && !isImage) {
        // ElMessage.error('请选择图片文件');
        console.log('请选择图片文件');
        return;
      }
      if (paramType === 'video' && !isVideo) {
        // ElMessage.error('请选择视频文件');
        console.log('请选择视频文件');
        return;
      }
      
      // 检查文件大小 (限制20MB)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        // ElMessage.error('文件太大，请选择小于20MB的文件');
        console.log('文件太大，请选择小于20MB的文件');
        return;
      }
      
      // 调用真实的文件上传API
      try {
        // ElMessage.info(`正在上传${paramType === 'image' ? '图片' : '视频'}...`);
        console.log(`正在上传${paramType === 'image' ? '图片' : '视频'}...`);
        
        const response = await uploadFiles(file, paramName);
        console.log('上传响应:', response);
        
        // 修复响应格式处理：uploadFiles返回 { success: true, data: [{ success: true, url: ... }] }
        if (response && response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
          const uploadResult = response.data[0];
          if (uploadResult.success && uploadResult.url) {
            templateParams.value[paramName] = uploadResult.url;
            // ElMessage.success(`${paramType === 'image' ? '图片' : '视频'}上传成功！可以预览了`);
            console.log(`${paramType === 'image' ? '图片' : '视频'}上传成功！可以预览了`);
            return;
          } else {
            throw new Error(`上传失败: ${uploadResult.error || '未知错误'}`);
          }
        } else if (response && !response.success) {
          throw new Error(`上传失败: ${response.error || '未知错误'}`);
        } else {
          throw new Error('上传响应格式错误');
        }
      } catch (uploadError) {
        console.error('文件上传失败:', uploadError);
        // ElMessage.warning(`文件上传失败，使用本地预览模式`);
        console.log(`文件上传失败，使用本地预览模式`);
        
        // 上传失败时，创建本地URL预览作为备选方案
        const url = URL.createObjectURL(file);
        templateParams.value[paramName] = url;
        console.log('创建本地预览URL:', url);
        // ElMessage.info('文件已选择，可以预览。生成视频时会自动重试上传');
        console.log('文件已选择，可以预览。生成视频时会自动重试上传');
      }
      
    } catch (error) {
      console.error('文件处理失败:', error);
      // ElMessage.error('文件处理失败，请重试');
      console.log('文件处理失败，请重试');
    }
  };
  
  document.body.appendChild(input);
  input.click();
  document.body.removeChild(input);
};

// 计算属性
const filteredTemplates = computed(() => {
  let filtered = templates.value;
  
  if (selectedCategory.value !== 'all') {
    filtered = filtered.filter(t => t.category === selectedCategory.value);
  }
  
  if (searchKeyword.value) {
    filtered = filtered.filter(t => 
      t.name.toLowerCase().includes(searchKeyword.value.toLowerCase()) ||
      t.description.toLowerCase().includes(searchKeyword.value.toLowerCase())
    );
  }
  
  return filtered;
});

// 当前选中模板的详细信息
const currentTemplate = computed(() => {
  if (!selectedTemplateKey.value) return null;
  return templates.value.find(t => t.key === selectedTemplateKey.value);
});

// 当前模板的参数列表
const currentTemplateParams = computed(() => {
  return currentTemplate.value?.parameters || [];
});

// 表单验证
const isFormValid = computed(() => {
  if (!currentTemplateParams.value.length) return true;
  return currentTemplateParams.value.every((param: { name: string; input_name?: string; required?: boolean }) => {
    if (!param.required) return true;
    const key = getParamKey(param);
    const value = templateParams.value[key];
    return value !== '' && value !== undefined && value !== null;
  });
});

// 方法

// 获取模板列表
const fetchTemplates = async () => {
  try {
    // 使用指定的活动模板接口，activity_id 固定为 7
    const response = await ActionAPI.getActivityTemplates(7, 'active', true);
    
    // 检查响应结构 - 活动模板接口返回的是 activity_templates
    if (response && response.data && response.data.activity_templates) {
      // 映射并按时间排序（最新的在前）
      const mappedTemplates = response.data.activity_templates.map((template: any) => ({
        key: template.template_id,
        name: template.template_name,
        description: template.description,
        image: template.thumbnail || template.cover || '/assets/main.png',
        tag: template.category,
        category: template.category || 'other',
        template_id: template.template_id,
        // 重要：保留参数信息
        parameters: template.parameters || [],
        // 新增字段用于预览弹窗
        video_url: template.example_video,
        sample_video: template.example_video,
        thumbnail: template.thumbnail,
        tags: template.tags || [],
        estimated_duration: template.estimated_duration,
        // 保留原始时间字段用于排序
        created_at: template.created_at,
        updated_at: template.updated_at
      }));
      
      // 按索引反序排列（4.3.2.1的顺序，最后的在最前面）
      templates.value = mappedTemplates.reverse();
      
      // 动态生成分类选项
      const categorySet = new Set(['all']);
      const categoryNames: Record<string, string> = {
        'all': '全部',
        'business': '商业',
        'creative': '创意',
        'education': '教育',
        'lifestyle': '生活',
        'entertainment': '娱乐',
        'marketing': '营销',
        'other': '其他'
      };
      
      templates.value.forEach(template => {
        if (template.category) {
          categorySet.add(template.category);
        }
      });
      
      categories.value = Array.from(categorySet).map(key => ({
        key,
        name: categoryNames[key] || key
      }));
    } else {
      console.error('活动模板响应数据格式不正确:', response);
      console.log('期望的数据路径: response.data.activity_templates');
      ElMessage.error('获取活动模板失败：数据格式错误');
    }
  } catch (error) {
    console.error('获取模板列表失败:', error);
    ElMessage.error('获取模板列表失败');
  }
};

// 获取用户配额信息
const fetchUserQuota = async () => {
  try {
    const userId = userStore.userInfo.id;
    if (!userId) return;
    
    // 获取会员信息
    const membershipResponse = await ActionAPI.getUserMemberships(Number(userId), 'active');
    // 获取加量包信息
    const packageResponse = await ActionAPI.getUserPackages(Number(userId), 'available');
    
    let dailyLimit = 0;
    let todayCalls = 0;
    let packageCalls = 0;
    let todayRemainingCalls = 0;
    let remainingCallsLastRefresh = null;
    
    if (membershipResponse.success && membershipResponse.data.user_memberships && membershipResponse.data.user_memberships.length > 0) {
      const membership = membershipResponse.data.user_memberships[0];
      dailyLimit = membership.daily_limit || 0;
      todayCalls = membership.today_calls || 0;
      // 使用新的 today_remaining_calls 字段（支持累加），如果没有则回退到旧逻辑
      todayRemainingCalls = membership.today_remaining_calls !== undefined 
        ? membership.today_remaining_calls 
        : (membership.remaining_calls !== undefined 
          ? membership.remaining_calls 
          : Math.max(0, dailyLimit - todayCalls));
      remainingCallsLastRefresh = membership.remaining_calls_last_refresh || null;
    }
    
    if (packageResponse.success && packageResponse.data.user_packages) {
      packageCalls = packageResponse.data.user_packages.reduce((sum: number, pkg: any) => sum + pkg.remaining_calls, 0);
    }
    
    userQuota.value = {
      dailyLimit,
      todayCalls,
      packageCalls,
      remainingCalls: todayRemainingCalls,
      todayRemainingCalls,
      remainingCallsLastRefresh
    };
  } catch (error) {
    console.error('获取用户配额失败:', error);
  }
};


const goBack = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  } else {
    router.back();
  }
};

const nextStep = () => {
  if (currentStep.value < 3) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const selectCategory = (category: string) => {
  selectedCategory.value = category;
};

const filterTemplates = () => {
  // 过滤逻辑在计算属性中处理
};

// 初始化模板参数
const initTemplateParams = (template: any) => {
  templateParams.value = {};
  if (template?.parameters) {
    template.parameters.forEach((param: {
      name: string;
      input_name?: string;
      data_type?: string;
      default?: any;
      required?: boolean;
    }) => {
      const key = getParamKey(param);
      // 检查是否为逗号分隔的选项参数
      if (hasCommaOptions(param)) {
        // 逗号分隔的参数：默认选择第一个选项
        const options = getCommaOptions(param);
        templateParams.value[key] = options.length > 0 ? options[0] : '';
      } else if (isUrlLikeParam(param)) {
        // URL/文件相关参数：强制为空，不使用默认值，只允许用户上传
        templateParams.value[key] = '';
      } else if (param.default !== undefined) {
        // 普通默认值处理（非URL参数）
        templateParams.value[key] = param.default;
      } else if (param.data_type === 'bool' || param.data_type === 'boolean') {
        templateParams.value[key] = false;
      } else if (param.data_type === 'int' || param.data_type === 'number' || param.data_type === 'float') {
        templateParams.value[key] = param.required ? 0 : '';
      } else {
        templateParams.value[key] = '';
      }
    });
  }
};

// 显示模板预览弹窗
const selectTemplate = (template: any) => {
  previewTemplate.value = template;
  showTemplatePreview.value = true;
};

// 确认使用模板
const confirmTemplate = () => {
  if (previewTemplate.value) {
    selectedTemplateKey.value = previewTemplate.value.key;
    selectedTemplate.value = previewTemplate.value;
    
    // 初始化参数
    initTemplateParams(previewTemplate.value);
    
    // 获取历史预估时间
    predictedDuration.value = getHistoricalDuration(previewTemplate.value.template_id || previewTemplate.value.key);
    
    // 关闭预览弹窗并进入下一步
    showTemplatePreview.value = false;
    nextStep();
  }
};

// 取消模板预览
const cancelTemplatePreview = () => {
  showTemplatePreview.value = false;
  previewTemplate.value = null;
};

// 这些方法已被动态参数系统替代，不再需要

// 构建请求参数（与PC端保持一致）
const buildRequestParameters = (): Record<string, any> => {
  if (!currentTemplate.value?.parameters) return {};
  
  const parameters: Record<string, any> = {};
  currentTemplate.value.parameters.forEach((param: {
    name: string;
    input_name?: string;
    data_type?: string;
    required?: boolean;
  }) => {
    const key = getParamKey(param);
    const dataType = String(param.data_type || '').toLowerCase();
    let value = templateParams.value[key];
    
    // 处理不同类型的值
    if (dataType === 'int' || dataType === 'number' || dataType === 'float') {
      if (value === '' || value === null || value === undefined) {
        // 数字类型：如果为空且不是必填项，则跳过
        if (!param.required) {
          return; // 跳过空值
        }
        value = 0; // 必填项默认设为0
      } else {
        const num = Number(value);
        value = Number.isFinite(num) ? num : value;
      }
    } else if (dataType === 'bool' || dataType === 'boolean') {
      // 布尔值始终包含，即使是 false
      value = Boolean(value);
    } else if (dataType === 'text' || dataType === 'string') {
      // 字符串类型：空字符串且不是必填项则跳过
      if ((value === '' || value === null || value === undefined) && !param.required) {
        return; // 跳过空值
      }
      value = value == null ? '' : String(value);
    } else {
      // 其他类型：空值且不是必填项则跳过
      if ((value === '' || value === null || value === undefined) && !param.required) {
        return; // 跳过空值
      }
    }
    
    // 只添加有值的参数（布尔值 false 也算有值）
    if (value !== '' && value !== null && value !== undefined) {
      parameters[key] = value;
    } else if (param.required) {
      // 必填项即使为空也要包含
      parameters[key] = value;
    }
  });
  
  console.log('构建的请求参数:', parameters);
  return parameters;
};

// 火箭启动动画处理
const handleRocketLaunch = async () => {
  if (!currentTemplate.value) {
    ElMessage.warning('请先选择模板');
    return;
  }
  
  // 验证必填参数
  if (!isFormValid.value) {
    ElMessage.warning('请填写所有必填参数');
    return;
  }

  // 开始火箭飞走动画
  isLaunching.value = true;
  
  // 等待动画完成后开始生成
  setTimeout(() => {
    isLaunching.value = false;
    startGeneration();
  }, 1500); // 动画持续1.5秒
};

const startGeneration = async () => {
  if (!currentTemplate.value) {
    ElMessage.warning('请先选择模板');
    return;
  }
  
  // 验证必填参数
  if (!isFormValid.value) {
    ElMessage.warning('请填写所有必填参数');
    return;
  }
  
  isGenerating.value = true;
  generationProgress.value = 0;
  generationStatus.value = '正在提交任务...';
  
  // 启动计时器
  startProgressTimers();
  
  // 重置进度详情
  progressDetail.value = {
    stage: '提交任务',
    message: '正在提交生成任务...',
    currentStep: 0,
    totalSteps: 0
  };
  
  try {
    const userId = userStore.userInfo.id;
    if (!userId) {
      throw new Error('用户未登录');
    }
    
    // 构建参数
    const parameters = buildRequestParameters();
    
    // 使用与PC端相同的API调用
    const payload = {
      user_id: Number(userId),
      template_id: currentTemplate.value.template_id || currentTemplate.value.key,
      activity_id: "7", // 固定设置为activity_id: "7"
      parameters: parameters
    };
    
    console.log('发送请求的完整payload:', JSON.stringify(payload, null, 2));
    console.log('模板参数详情:', currentTemplate.value?.parameters);
    console.log('当前templateParams:', templateParams.value);
    
    const response = await ActionAPI.executeTemplate(payload.user_id, payload.template_id, payload.activity_id, payload.parameters);
    
    if (response.success) {
      const taskId = response.data?.task_id;
      if (taskId) {
        generationStatus.value = '任务已提交，正在生成中...';
        await pollTaskStatus(taskId);
      } else {
        throw new Error('未获取到任务ID');
      }
    } else {
      throw new Error(response.error || '任务提交失败');
    }
  } catch (error: any) {
    console.error('视频生成失败:', error);
    ElMessage.error(error.message || '视频生成失败，请重试');
    isGenerating.value = false;
    stopProgressTimers();
  }
};

// 轮询任务状态
const pollTaskStatus = async (taskId: string) => {
  const maxAttempts = 120; // 最大轮询次数（10分钟）
  let attempts = 0;
  
  const poll = async () => {
    try {
      attempts++;
      const response = await ActionAPI.getTaskStatus(taskId);
      
      if (response.success) {
        const status = response.data.status;
        const progress = response.data.progress || 0;
        
        generationProgress.value = Math.min(progress, 95); // 最大显示95%
        
        // 更新进度详情
        progressDetail.value.currentStep = response.data.current_step || progressDetail.value.currentStep;
        progressDetail.value.totalSteps = response.data.total_steps || progressDetail.value.totalSteps;
        progressDetail.value.message = response.data.message || progressDetail.value.message;
        
        switch (status) {
          case 'pending':
            generationStatus.value = '任务排队中...';
            progressDetail.value.stage = '排队等待';
            estimatedTime.value = Math.max(5 - attempts * 0.1, 0.5);
            break;
          case 'running':
            generationStatus.value = '正在生成视频...';
            progressDetail.value.stage = '生成中';
            // 根据历史数据动态调整预估时间
            const histDuration = predictedDuration.value;
            if (histDuration && elapsedSeconds.value > 0) {
              const remainingTime = Math.max(histDuration - elapsedSeconds.value, 10);
              estimatedTime.value = Math.max(remainingTime / 60, 0.2); // 转换为分钟
            } else {
              estimatedTime.value = Math.max(8 - attempts * 0.1, 1);
            }
            break;
          case 'completed':
            generationProgress.value = 100;
            generationStatus.value = '生成完成！';
            progressDetail.value.stage = '完成';
            progressDetail.value.message = '视频生成成功！';
            
            // 停止计时器并保存历史时长
            stopProgressTimers();
            if (selectedTemplate.value && elapsedSeconds.value > 0) {
              saveHistoricalDuration(
                selectedTemplate.value.template_id || selectedTemplate.value.key,
                elapsedSeconds.value
              );
            }
            
            // 调试：打印完整的响应数据
            console.log('任务完成响应数据:', response.data);
            
            // 获取视频URL，根据后端实际返回结构提取
            const videoUrl = response.data.result?.description ||  // 主要字段
                            response.data.result_url || 
                            response.data.output_url || 
                            response.data.video_url || 
                            response.data.file_url ||
                            response.data.url;
            
            // 获取缩略图URL
            const thumbnailUrl = response.data.result?.first_frame_image || 
                               response.data.thumbnail_url || 
                               videoUrl; // 如果没有缩略图，使用视频URL
            
            console.log('提取的视频URL:', videoUrl);
            console.log('提取的缩略图URL:', thumbnailUrl);
            
            if (!videoUrl) {
              console.error('未找到视频URL，完整响应:', response.data);
              ElMessage.error('视频生成完成，但无法获取视频地址');
              isGenerating.value = false;
              return;
            }
            
            // 设置生成结果
            generatedVideo.value = {
              id: taskId,
              url: videoUrl,
              thumbnail: thumbnailUrl,
              title: response.data.template_name || `AI视频_${new Date().toLocaleString()}`,
              createdAt: new Date(response.data.created_at || Date.now())
            };
            
            console.log('设置的生成视频对象:', generatedVideo.value);
            
            isGenerating.value = false;
            ElMessage.success('视频生成完成！');
            
            // 刷新用户配额信息
            fetchUserQuota();
            return;
          case 'failed':
            progressDetail.value.stage = '失败';
            progressDetail.value.message = response.data.error_message || '任务执行失败';
            stopProgressTimers();
            throw new Error(response.data.error_message || '任务执行失败');
          default:
            generationStatus.value = `任务状态: ${status}`;
        }
        
        // 继续轮询
        if (attempts < maxAttempts && status !== 'completed' && status !== 'failed') {
          setTimeout(poll, 5000); // 5秒后再次查询
        } else if (attempts >= maxAttempts) {
          throw new Error('任务超时，请稍后查看我的视频');
        }
      } else {
        throw new Error('查询任务状态失败');
      }
    } catch (error: any) {
      console.error('查询任务状态失败:', error);
      isGenerating.value = false;
      stopProgressTimers();
      ElMessage.error(error.message || '任务执行失败');
    }
  };
  
  // 开始轮询
  poll();
};

const downloadVideo = async () => {
  if (!generatedVideo.value || !generatedVideo.value.url) {
    ElMessage.error('视频地址不存在，无法下载');
    return;
  }

  // 防止重复下载
  if (isDownloading.value) {
    return;
  }

  isDownloading.value = true;

  try {
    ElMessage.info('正在准备下载...');
    
    // 通过fetch获取视频数据，避免CORS问题
    const response = await fetch(generatedVideo.value.url);
    if (!response.ok) {
      throw new Error('下载失败');
    }
    
    // 将响应转换为Blob
    const blob = await response.blob();
    
    // 创建对象URL
    const blobUrl = URL.createObjectURL(blob);
    
    // 创建下载链接
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `神小智AI视频_${generatedVideo.value.title || Date.now()}.mp4`;
    
    // 添加到DOM，触发下载，然后移除
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 清理对象URL
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 100);
    
    ElMessage.success('视频下载已开始');
  } catch (error) {
    console.error('下载失败:', error);
    
    // 降级方案：直接使用原始URL下载
    try {
      const link = document.createElement('a');
      link.href = generatedVideo.value.url;
      link.download = `神小智AI视频_${generatedVideo.value.title || Date.now()}.mp4`;
      link.target = '_blank'; // 如果下载失败，在新窗口打开
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      ElMessage.warning('正在尝试下载，如果浏览器打开了新页面，请手动保存视频');
    } catch (fallbackError) {
      console.error('降级下载也失败:', fallbackError);
      ElMessage.error('下载失败，请尝试复制链接后手动下载');
    }
  } finally {
    // 2秒后重置下载状态，给用户足够的反馈时间
    setTimeout(() => {
      isDownloading.value = false;
    }, 2000);
  }
};

// 复制页面链接到剪贴板的辅助函数
const copyUrlToClipboard = async () => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(window.location.href);
      ElMessage.success('链接已复制到剪贴板');
    } else {
      // 降级方案：使用传统的复制方法
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          ElMessage.success('链接已复制到剪贴板');
        } else {
          ElMessage.error('复制失败，请手动复制链接');
        }
      } catch (error) {
        console.error('复制失败:', error);
        ElMessage.error('复制失败，请手动复制链接');
      } finally {
        document.body.removeChild(textArea);
      }
    }
  } catch (error) {
    console.error('复制链接失败:', error);
    ElMessage.error('复制失败，请手动复制链接');
  }
};

const createNew = () => {
  // 重置状态
  currentStep.value = 1;
  selectedTemplateKey.value = '';
  selectedTemplate.value = null;
  templateParams.value = {};
  generatedVideo.value = null;
  generationProgress.value = 0;
};


// 视频加载事件处理
const handleVideoLoadStart = () => {
  console.log('视频开始加载...');
};

const handleVideoLoadedMetadata = () => {
  console.log('视频元数据加载完成');
};

const handleVideoError = (event: any) => {
  console.error('视频加载失败:', event);
  console.error('视频错误详情:', event.target.error);
  
  // 检查错误类型
  const error = event.target.error;
  if (error) {
    if (error.code === 4) { // MEDIA_ELEMENT_ERROR
      ElMessage.error({
        message: '视频播放失败，可能是网络问题。您可以尝试复制链接或下载视频观看',
        duration: 6000,
        showClose: true
      });
    } else {
      ElMessage.error('视频播放失败，请检查网络连接或联系客服');
    }
  } else {
    ElMessage.error('视频播放失败，请检查网络连接或联系客服');
  }
};

const handleVideoCanPlay = () => {
  console.log('视频可以开始播放');
};

// 复制视频链接
const copyVideoUrl = async () => {
  if (generatedVideo.value && generatedVideo.value.url) {
    try {
      await navigator.clipboard.writeText(generatedVideo.value.url);
      ElMessage.success('视频链接已复制到剪贴板');
    } catch (error) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = generatedVideo.value.url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        ElMessage.success('视频链接已复制到剪贴板');
      } catch (fallbackError) {
        ElMessage.error('复制失败，请手动复制链接');
        console.error('复制失败:', fallbackError);
      }
      document.body.removeChild(textArea);
    }
  }
};

// 重新加载视频
const reloadVideo = () => {
  if (generatedVideo.value && generatedVideo.value.url) {
    const videoElement = document.querySelector('.result-video, .preview-video') as HTMLVideoElement;
    if (videoElement) {
      videoElement.load(); // 重新加载视频
      ElMessage.info('正在重新加载视频...');
    }
  }
};

// 返回视频详情步骤
const backToVideoStep = () => {
  currentStep.value = 3;
};

// 判断是否为URL参数
const isUrlParam = (param: any): boolean => {
  return isUrlLikeParam(param);
};

// 获取参数对应的图标
const getParamIcon = (param: any): string => {
  const name = String(param.name || '').toLowerCase();
  const label = String(param.label || '').toLowerCase();
  const dataType = String(param.data_type || '').toLowerCase();
  
  // 根据参数类型返回对应图标
  if (dataType === 'bool' || dataType === 'boolean') {
    return 'fas fa-toggle-on';
  }
  
  // 逗号分隔的选择参数
  if (hasCommaOptions(param)) {
    return 'fas fa-list-ul';
  }
  
  if (isUrlLikeParam(param)) {
    if (determineParamType(param) === 'image') {
      return 'fas fa-image';
    } else if (determineParamType(param) === 'video') {
      return 'fas fa-video';
    } else {
      return 'fas fa-link';
    }
  }
  
  if (dataType === 'int' || dataType === 'number' || dataType === 'float') {
    return 'fas fa-hashtag';
  }
  
  if (name.includes('签名') || name.includes('描述') || name.includes('prompt')) {
    return 'fas fa-quote-left';
  }
  
  if (name.includes('时间') || name.includes('duration')) {
    return 'fas fa-clock';
  }
  
  if (name.includes('样式') || name.includes('style')) {
    return 'fas fa-palette';
  }
  
  return 'fas fa-cog';
};

// 获取显示用的URL（截断长URL）
const getDisplayUrl = (url: string): string => {
  if (!url) return '';
  if (url.length <= 40) return url;
  
  // 尝试提取文件名
  const parts = url.split('/');
  const filename = parts[parts.length - 1];
  
  if (filename && filename.length <= 30) {
    return `.../${filename}`;
  }
  
  // 如果文件名也很长，截断显示
  return url.substring(0, 20) + '...' + url.substring(url.length - 15);
};

// 复制到剪贴板
const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      ElMessage.success('已复制到剪贴板');
    } else {
      // 降级方案：使用传统的复制方法
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          ElMessage.success('已复制到剪贴板');
        } else {
          ElMessage.error('复制失败，请手动复制');
        }
      } catch (error) {
        console.error('复制失败:', error);
        ElMessage.error('复制失败，请手动复制');
      } finally {
        document.body.removeChild(textArea);
      }
    }
  } catch (error) {
    console.error('复制到剪贴板失败:', error);
    ElMessage.error('复制失败，请手动复制');
  }
};

// 处理图片加载错误
const handleImageError = (event: any) => {
  console.log('图片加载失败:', event.target.src);
  event.target.style.display = 'none';
  // 可以添加一个错误占位符
  const errorDiv = document.createElement('div');
  errorDiv.className = 'image-error';
  errorDiv.innerHTML = '<i class="fas fa-exclamation-triangle"></i><span>图片加载失败</span>';
  event.target.parentNode.appendChild(errorDiv);
};

// 在新窗口打开图片
const openImageInNewTab = (imageUrl: string) => {
  if (imageUrl) {
    window.open(imageUrl, '_blank');
  }
};

// 历史时长管理
const getHistoricalDuration = (templateId: string): number | null => {
  try {
    const raw = localStorage.getItem('mobile_vc_hist_durations');
    const obj = raw ? JSON.parse(raw) : {};
    const val = obj?.[templateId];
    return typeof val === 'number' ? val : null;
  } catch {
    return null;
  }
};

const saveHistoricalDuration = (templateId: string, durationSec: number) => {
  if (!Number.isFinite(durationSec)) return;
  try {
    const raw = localStorage.getItem('mobile_vc_hist_durations');
    const obj = raw ? JSON.parse(raw) : {};
    const prev = obj?.[templateId];
    // 使用加权平均：60%历史数据 + 40%新数据
    const next = typeof prev === 'number' ? (0.6 * prev + 0.4 * durationSec) : durationSec;
    obj[templateId] = Math.round(next);
    localStorage.setItem('mobile_vc_hist_durations', JSON.stringify(obj));
  } catch {
    // ignore
  }
};

// 时间和小贴士管理
const startProgressTimers = () => {
  startTimestamp.value = Date.now();
  elapsedSeconds.value = 0;
  
  // 清除已有的定时器
  if (elapsedTimer.value) {
    clearInterval(elapsedTimer.value);
  }
  
  // 启动已用时间计时器
  elapsedTimer.value = setInterval(() => {
    if (startTimestamp.value) {
      elapsedSeconds.value = Math.floor((Date.now() - startTimestamp.value) / 1000);
    }
  }, 1000);
};

const stopProgressTimers = () => {
  if (elapsedTimer.value) {
    clearInterval(elapsedTimer.value);
    elapsedTimer.value = null;
  }
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// 页面加载时的初始化
onMounted(async () => {
  // 加载主题设置
  loadTheme();
  
  // 等待用户状态初始化完成
  await new Promise<void>(resolve => {
    const checkUserState = () => {
      if (userStore.token || localStorage.getItem('token')) {
        resolve();
      } else {
        // 等待50ms后再次检查，最多等待2秒
        setTimeout(() => {
          if (userStore.token || localStorage.getItem('token')) {
            resolve();
          } else if (Date.now() - startTime > 2000) {
            resolve(); // 超时也继续执行
          } else {
            checkUserState();
          }
        }, 50);
      }
    };
    const startTime = Date.now();
    checkUserState();
  });
  
  // 检查用户是否登录
  if (!userStore.token) {
    ElMessage.warning('请先登录');
    router.push('/pages/mobile/login?redirect=' + encodeURIComponent(router.currentRoute.value.fullPath));
    return;
  }
  
  // 获取页面数据
  fetchTemplates();
  fetchUserQuota();
});

// 组件卸载时的清理
onUnmounted(() => {
  stopProgressTimers();
});
</script>

<style scoped lang="scss">
.mobile-video-create {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-attachment: fixed; /* 确保背景固定 */
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 为了确保背景色覆盖整个页面高度 */
.mobile-video-create::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: -1;
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 0;
  z-index: 100;
  
  .back-btn, .videos-btn {
    width: 40px;
    height: 40px;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    color: white;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
  
  .header-center {
    flex: 1;
    text-align: center;
    
    h1 {
      color: white;
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 4px 0;
    }
    
    .quota-info {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      color: rgba(255, 255, 255, 0.8);
      font-size: 14px;
      
      i {
        color: #ffd700;
      }
    }
  }
}

.main-content {
  flex: 1;
  padding: 20px 16px;
  position: relative;
  z-index: 1; /* 确保内容在背景之上 */
}

.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  
  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    
    .step-number {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.2);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      transition: all 0.3s ease;
    }
    
    span {
      color: rgba(255, 255, 255, 0.7);
      font-size: 12px;
      text-align: center;
    }
    
    &.active .step-number {
      background: linear-gradient(135deg, #667eea, #764ba2);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
    
    &.active span {
      color: white;
      font-weight: 600;
    }
    
    &.completed .step-number {
      background: #10b981;
      
      &::after {
        content: '✓';
      }
    }
  }
  
  .step-line {
    width: 40px;
    height: 2px;
    background: rgba(255, 255, 255, 0.2);
    margin: 0 10px;
  }
}

.step-content {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.section-header {
  text-align: center;
  margin-bottom: 24px;
  
  h2 {
    color: #333;
    font-size: 24px;
    font-weight: bold;
    margin: 0 0 8px 0;
  }
  
  p {
    color: #666;
    font-size: 16px;
    margin: 0;
  }
}

// 模板选择步骤样式
.search-bar {
  position: relative;
  margin-bottom: 20px;
  
  i {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
  }
  
  input {
    width: 100%;
    height: 48px;
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 0 16px 0 48px;
    font-size: 16px;
    
    &:focus {
      outline: none;
      border-color: #667eea;
    }
  }
}

.category-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  
  .category-tab {
    padding: 8px 16px;
    background: #f8f9fa;
    border: none;
    border-radius: 20px;
    color: #666;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.3s ease;
    
    &.active {
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
    }
  }
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.template-card {
  border: 2px solid transparent;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  
  &.selected {
    border-color: #667eea;
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.2);
  }
  
  .template-image {
    position: relative;
    aspect-ratio: 16/9;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .template-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      
      i {
        color: white;
        font-size: 24px;
      }
    }
    
    &:hover .template-overlay {
      opacity: 1;
    }
  }
  
  .template-info {
    padding: 12px;
    
    h3 {
      color: #333;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 4px 0;
    }
    
    p {
      color: #666;
      font-size: 14px;
      margin: 0 0 8px 0;
      line-height: 1.4;
    }
    
    .template-tag {
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 12px;
    }
  }
}

// 参数设置步骤样式
.selected-template-info {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 24px;
  
  img {
    width: 80px;
    height: 60px;
    border-radius: 8px;
    object-fit: cover;
  }
  
  .template-details {
    flex: 1;
    
    h3 {
      color: #333;
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 4px 0;
    }
    
    p {
      color: #666;
      font-size: 14px;
      margin: 0;
    }
  }
}

.parameter-form {
  .form-group {
    margin-bottom: 20px;
    
    label {
      display: block;
      color: #333;
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      
      .required {
        color: #dc3545;
      }
    }
    
    textarea, select {
      width: 100%;
      background: #f8f9fa;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      padding: 12px;
      font-size: 16px;
      color: #333;
      resize: vertical;
      
      &:focus {
        outline: none;
        border-color: #667eea;
      }
    }
    
    textarea {
      min-height: 100px;
    }
    
    .char-count {
      text-align: right;
      color: #999;
      font-size: 14px;
      margin-top: 4px;
    }
  }

  // 动态参数样式
  .param-group {
    margin-bottom: 24px;
    
    .param-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: #333;
      font-size: 16px;
      
      .required {
        color: #ff4757;
        margin-left: 4px;
      }
    }
    
    .form-input, .form-textarea, .form-select {
      width: 100%;
      padding: 12px;
      border: 2px solid #e1e5e9;
      border-radius: 8px;
      font-size: 16px;
      background: #f8f9fa;
      color: #333;
      transition: all 0.3s ease;
      
      &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      }
      
      &[readonly] {
        background: #e9ecef !important;
        color: #6c757d !important;
        cursor: not-allowed;
        border-color: #dee2e6 !important;
        
        &:focus {
          border-color: #dee2e6 !important;
          box-shadow: none !important;
        }
        
        &::placeholder {
          color: #868e96 !important;
        }
      }
    }
    
    .form-textarea {
      resize: vertical;
      line-height: 1.5;
      min-height: 100px;
    }

    // 选项按钮组样式
    .option-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;

      .option-btn {
        flex: 1;
        min-width: 0;
        padding: 10px 16px;
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        background: #f8f9fa;
        color: #333;
        font-size: 14px;
        font-weight: 500;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:hover {
          border-color: #667eea;
          background: #e3f2fd;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(102, 126, 234, 0.2);
        }

        &.active {
          border-color: #667eea;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);

          &:hover {
            background: linear-gradient(135deg, #5a67d8, #6b46c1);
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
          }
        }

        // 响应式：小屏幕时使用更紧凑的布局
        @media (max-width: 480px) {
          padding: 8px 12px;
          font-size: 13px;
          flex: 1 1 auto;
          min-width: calc(50% - 4px);
        }
      }

      // 当选项较多时，改为垂直布局
      &:has(.option-btn:nth-child(4)) {
        .option-btn {
          flex: 1 1 calc(50% - 4px);
        }
      }

      &:has(.option-btn:nth-child(6)) {
        .option-btn {
          flex: 1 1 calc(33.333% - 6px);
        }
      }
    }
    
    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      
      .form-checkbox {
        width: auto;
        margin: 0;
      }
      
      .checkbox-text {
        color: #666;
        font-size: 16px;
      }
    }
    
    .url-input-group {
      display: flex;
      gap: 8px;
      
      .form-input {
        flex: 1;
      }
      
      .upload-btn {
        padding: 12px 16px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
      }
    }
    
    .param-description {
      font-size: 14px;
      color: #999;
      margin-top: 8px;
      line-height: 1.4;
    }

    .file-preview {
      margin-top: 12px;
      
      .preview-item {
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;
        
        &:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }
        
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          
          .preview-status {
            .status-badge {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 4px 10px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 500;
              
              &.status-uploaded {
                background: #dcfce7;
                color: #166534;
                border: 1px solid #bbf7d0;
                
                i {
                  color: #22c55e;
                }
              }
              
              &.status-local {
                background: #fef3c7;
                color: #92400e;
                border: 1px solid #fde68a;
                
                i {
                  color: #f59e0b;
                }
              }
            }
          }
          
          .remove-btn {
            width: 32px;
            height: 32px;
            background: #fee2e2;
            color: #dc2626;
            border: 1px solid #fecaca;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            
            &:hover {
              background: #fecaca;
              color: #991b1b;
              transform: scale(1.05);
            }
            
            i {
              font-size: 14px;
            }
          }
        }
        
        .preview-content {
          padding: 16px;
          text-align: center;
          
          .preview-image, .preview-video {
            max-width: 100%;
            max-height: 200px;
            width: auto;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          
          .preview-video {
            background: #000;
          }
        }
        
        .preview-footer {
          padding: 12px 16px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          
          .file-info {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #64748b;
            font-size: 13px;
            
            i {
              color: #4f46e5;
            }
            
            .file-type {
              font-weight: 500;
            }
          }
        }
      }
    }
  }
}

// 无参数提示样式
.no-params-tip {
  text-align: center;
  padding: 40px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  color: #666;
  
  i {
    font-size: 24px;
    margin-bottom: 12px;
    color: #667eea;
  }
  
  p {
    margin: 0;
    font-size: 16px;
  }
}

.duration-options, .style-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.duration-btn, .style-btn {
  padding: 12px;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.active {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border-color: transparent;
  }
}

.style-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  
  i {
    font-size: 20px;
  }
  
  span {
    font-size: 14px;
  }
}

.advanced-settings {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

// 生成视频步骤样式
.generation-preview {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  padding: 0;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  
  .preview-header {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    
    i {
      font-size: 18px;
    }
    
    h4 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }
  }
  
  .preview-content {
    padding: 20px;
  }
  
  .preview-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.95);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }
    
    &.template-item {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
      border: 1px solid rgba(102, 126, 234, 0.2);
    }
    
    .item-icon {
      width: 36px;
      height: 36px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;
      
      i {
        color: white;
        font-size: 14px;
      }
    }
    
    .item-content {
      flex: 1;
      min-width: 0;
      
      .label {
        display: block;
        font-weight: 600;
        color: #2d3748;
        font-size: 13px;
        margin-bottom: 4px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .value {
        color: #4a5568;
        font-size: 14px;
        line-height: 1.4;
        word-break: break-all;
        
        &.url-value {
          word-break: break-word;
        }
      }
    }
  }
  
  // URL显示样式
  .url-display {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f7fafc;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    
    .url-text {
      flex: 1;
      font-family: monospace;
      font-size: 12px;
      color: #2d3748;
    }
    
    .copy-url-btn {
      background: none;
      border: none;
      color: #667eea;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s ease;
      
      &:hover {
        background: #667eea;
        color: white;
      }
      
      i {
        font-size: 12px;
      }
    }
  }
  
  // 布尔值样式
  .bool-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    
    &.bool-true {
      background: #c6f6d5;
      color: #22543d;
      border: 1px solid #9ae6b4;
    }
    
    &.bool-false {
      background: #fed7d7;
      color: #742a2a;
      border: 1px solid #fc8181;
    }
    
    i {
      font-size: 10px;
    }
  }
  
  // 长文本样式
  .long-text {
    display: inline-block;
    max-width: 100%;
    cursor: help;
    padding: 2px 0;
    border-bottom: 1px dotted #a0aec0;
  }
  
  // 图片预览样式
  .image-preview {
    position: relative;
    display: inline-block;
    background: #f7fafc;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    
    .preview-img {
      max-width: 120px;
      max-height: 80px;
      width: auto;
      height: auto;
      display: block;
      object-fit: cover;
      border-radius: 7px;
    }
    
    .image-actions {
      position: absolute;
      top: 4px;
      right: 4px;
      display: flex;
      gap: 4px;
      opacity: 0;
      transition: opacity 0.3s ease;
      
      .copy-url-btn, .open-image-btn {
        width: 24px;
        height: 24px;
        background: rgba(0, 0, 0, 0.7);
        border: none;
        border-radius: 4px;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        
        &:hover {
          background: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }
        
        i {
          font-size: 10px;
        }
      }
      
      .open-image-btn {
        background: rgba(102, 126, 234, 0.8);
        
        &:hover {
          background: rgba(102, 126, 234, 1);
        }
      }
    }
    
    &:hover .image-actions {
      opacity: 1;
    }
    
    .image-error {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 12px;
      color: #e53e3e;
      font-size: 12px;
      background: #fff5f5;
      
      i {
        color: #e53e3e;
      }
    }
  }
}

.generation-status {
  text-align: center;
  padding: 40px 20px;
  position: relative;
  min-height: 300px; /* 防止高度跳跃 */
}

// 过渡动画样式
.fade-slide-enter-active {
  transition: all 0.8s cubic-bezier(0.23, 1, 0.320, 1);
  transition-delay: 0.1s; /* 轻微延迟让动画更自然 */
}

.fade-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(40px) scale(0.9);
  filter: blur(2px);
}

.fade-slide-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0px);
}

.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(1.02);
  filter: blur(1px);
}

// 为每个状态组件添加独立的动画细节
.generation-progress {
  .progress-icon {
    animation: progressBounce 1.5s ease-in-out infinite;
  }
}

.generation-complete {
  animation: completeBounceIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes readyPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes progressBounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

@keyframes completeBounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3) translateY(50px);
  }
  50% {
    opacity: 1;
    transform: scale(1.05) translateY(-10px);
  }
  70% {
    transform: scale(0.9) translateY(5px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.generation-ready {
  animation: readyPulse 2s ease-in-out infinite;
  
  .ready-content {
    h3 {
      color: #333;
      font-size: 24px;
      margin: 0 0 12px 0;
    }
    
    p {
      color: #666;
      font-size: 16px;
      margin: 0 0 32px 0;
    }
  }

  .rocket-launch-container {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    .rocket-btn {
      width: 100px;
      height: 100px;
      border: none;
      border-radius: 50%;
      background: linear-gradient(135deg, #ff6b6b, #ee5a24);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 8px 25px rgba(255, 107, 107, 0.3);

      &:hover:not(:disabled) {
        transform: translateY(-4px) scale(1.05);
        box-shadow: 0 12px 35px rgba(255, 107, 107, 0.4);
        background: linear-gradient(135deg, #ee5a24, #ff6b6b);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.7;
      }

      .rocket-icon {
        font-size: 36px;
        color: white;
        transition: all 0.3s ease;
        transform: rotate(-45deg);
        
        &.fly-away {
          animation: rocketLaunch 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
      }

      &.launching {
        animation: buttonPulse 1.5s ease forwards;
        
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 4px;
          height: 4px;
          background: #ffd700;
          border-radius: 50%;
          animation: sparkles 1.5s ease infinite;
        }

        &::after {
          content: '';
          position: absolute;
          top: 60%;
          left: 45%;
          width: 20px;
          height: 6px;
          background: linear-gradient(90deg, transparent, #ff9500, transparent);
          border-radius: 3px;
          animation: exhaust 1.5s ease forwards;
          opacity: 0;
        }
      }
    }
  }

  @keyframes rocketLaunch {
    0% {
      transform: rotate(-45deg) translateY(0) scale(1);
      opacity: 1;
    }
    30% {
      transform: rotate(-35deg) translateY(-10px) scale(1.1);
    }
    70% {
      transform: rotate(-25deg) translateY(-150px) scale(0.8);
      opacity: 0.8;
    }
    100% {
      transform: rotate(-15deg) translateY(-300px) scale(0.3);
      opacity: 0;
    }
  }

  @keyframes buttonPulse {
    0% {
      transform: scale(1);
      background: linear-gradient(135deg, #ff6b6b, #ee5a24);
    }
    50% {
      transform: scale(1.1);
      background: linear-gradient(135deg, #ffd700, #ff9500);
    }
    100% {
      transform: scale(1);
      background: linear-gradient(135deg, #ff6b6b, #ee5a24);
    }
  }

  @keyframes sparkles {
    0%, 100% {
      transform: translate(-50%, -50%) scale(0);
      opacity: 0;
    }
    50% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
  }

  @keyframes exhaust {
    0% {
      opacity: 0;
      transform: translateY(0) scale(1);
    }
    30% {
      opacity: 1;
      transform: translateY(15px) scale(1.2);
    }
    100% {
      opacity: 0;
      transform: translateY(60px) scale(0.8);
    }
  }
}

.generation-progress {
  .progress-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 20px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    i {
      font-size: 32px;
      color: white;
    }
  }
  
  h3 {
    color: #333;
    font-size: 20px;
    margin: 0 0 20px 0;
  }
  
  .progress-bar {
    width: 100%;
    height: 10px;
    background: #e9ecef;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 12px;
    position: relative;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 6px;
      transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.4),
          transparent
        );
        animation: shimmer 2s infinite;
      }
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  
  .progress-text {
    color: #666;
    font-size: 16px;
    margin: 0 0 8px 0;
  }
  
  .estimated-time {
    color: #999;
    font-size: 14px;
  }

  .simple-time-info {
    margin: 16px 0;
    text-align: center;
    
    .time-item {
      margin-bottom: 8px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      span {
        display: inline-block;
        font-size: 14px;
        color: #666;
        padding: 4px 8px;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 6px;
      }
    }
  }
}

.generation-complete {
  .video-result {
    text-align: center;
    padding: 24px 16px; // 增加内边距，避免紧贴边框
    
    h3 {
      color: #2c3e50;
      margin-bottom: 12px;
      margin-top: 0; // 确保标题顶部不会太紧贴
      font-size: 20px;
    }
    
    p {
      color: #666;
      margin-bottom: 20px;
    }
    
    
    .video-error {
      background: #fff5f5;
      border: 1px solid #feb2b2;
      border-radius: 8px;
      padding: 16px;
      margin: 16px 0;
      
      i {
        color: #e53e3e;
        font-size: 24px;
        margin-bottom: 8px;
      }
      
      p {
        color: #c53030;
        margin: 0;
      }
    }
    
    .result-video {
      width: 100%;
      max-width: 400px;
      border-radius: 12px;
      margin-bottom: 20px;
    }
    
    .video-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      max-width: 360px;
      margin: 24px auto 8px auto;
      padding: 0 16px;
      
      button {
        padding: 12px 16px;
        border: none;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
        
        i {
          font-size: 16px;
        }
        
        &.open-btn {
          background: linear-gradient(135deg, #48bb78, #38a169);
          color: white;
          
          &:hover {
            background: linear-gradient(135deg, #38a169, #2f855a);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
          }
        }
        
        &.copy-btn {
          background: linear-gradient(135deg, #4299e1, #3182ce);
          color: white;
          
          &:hover {
            background: linear-gradient(135deg, #3182ce, #2c5aa0);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(66, 153, 225, 0.4);
          }
        }
        
        &.download-btn {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          
          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #764ba2, #553c9a);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          }
          
          &.downloading {
            background: linear-gradient(135deg, #ffa500, #ff8c00) !important;
            cursor: not-allowed;
            
            i {
              margin-right: 8px;
            }
          }
          
          &:disabled {
            opacity: 0.8;
            cursor: not-allowed;
          }
        }
        
        &.share-btn {
          background: linear-gradient(135deg, #ed8936, #dd6b20);
          color: white;
          
          &:hover {
            background: linear-gradient(135deg, #dd6b20, #c05621);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(237, 137, 54, 0.4);
          }
        }
        
        &.reload-btn {
          background: linear-gradient(135deg, #ffc107, #e0a800);
          color: #212529;
          
          &:hover {
            background: linear-gradient(135deg, #e0a800, #d39e00);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(255, 193, 7, 0.4);
          }
          
          &.full-width {
            grid-column: 1 / -1; // 跨越所有列
          }
        }
      }
    }
  }
}

.step-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  
  button {
    padding: 12px 24px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    &.prev-btn {
      background: #f8f9fa;
      color: #666;
      border: 2px solid #e9ecef;
      
      &:hover:not(:disabled) {
        background: #e9ecef;
      }
    }
    
    &.next-btn, &.generate-btn, &.new-btn {
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
      
      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 12px 25px rgba(102, 126, 234, 0.4);
      }
    }
  }
}

// 弹窗样式
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  padding: 0;
  
  // 模板预览弹窗特殊样式
  &.template-preview-overlay {
    align-items: center;
    padding: 20px;
  }
}

.modal-content {
  background: white;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-height: 70vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  // 模板预览弹窗特殊样式
  &.template-preview-modal {
    border-radius: 16px;
    max-height: 85vh;
    max-width: 500px;
    margin: 0 auto;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: between;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  
  h3 {
    color: #333;
    font-size: 20px;
    margin: 0;
    flex: 1;
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #f8f9fa;
    border-radius: 50%;
    color: #666;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #e9ecef;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

// 模板预览弹窗专门样式
.template-preview-body {
  padding: 0;
  
  .template-media {
    position: relative;
    
    .sample-video, .template-image {
      width: 100%;
      text-align: center;
      background: #000;
      
      video, img {
        width: 100%;
        height: 250px;
        object-fit: cover;
        border-radius: 0;
      }
    }
  }
  
  .template-info {
    padding: 20px;
    
    .template-description {
      margin-bottom: 20px;
      
      h4 {
        margin: 0 0 8px 0;
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }
      
      p {
        margin: 0;
        font-size: 14px;
        color: #666;
        line-height: 1.5;
      }
    }
    
    .template-tags {
      margin-bottom: 20px;
      
      h4 {
        margin: 0 0 8px 0;
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }
      
      .tags-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        
        .tag {
          padding: 4px 8px;
          background: #e9ecef;
          border-radius: 12px;
          font-size: 12px;
          color: #495057;
        }
      }
    }
    
    .template-params-info {
      margin-bottom: 20px;
      
      h4 {
        margin: 0 0 8px 0;
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }
      
      p {
        margin: 0 0 12px 0;
        font-size: 13px;
        color: #666;
      }
      
      .params-preview {
        .param-item, .more-params {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 0;
          font-size: 13px;
          color: #555;
          
          i {
            color: #6c757d;
            width: 14px;
          }
          
          .required-mark {
            color: #dc3545;
            font-weight: bold;
          }
        }
        
        .more-params {
          color: #999;
          font-style: italic;
        }
      }
    }
    
    .template-duration {
      h4 {
        margin: 0 0 8px 0;
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }
      
      p {
        margin: 0;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #666;
        
        i {
          color: #6c757d;
        }
      }
    }
  }
}

.template-preview-footer {
  padding: 20px;
  display: flex;
  gap: 12px;
  border-top: 1px solid #e9ecef;
  
  .cancel-btn, .confirm-btn {
    flex: 1;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    
    i {
      font-size: 12px;
    }
  }
  
  .cancel-btn {
    background: #f8f9fa;
    color: #6c757d;
    border: 1px solid #e9ecef;
    
    &:hover {
      background: #e9ecef;
      color: #495057;
    }
  }
  
  .confirm-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .main-content {
    padding: 16px 12px;
  }
  
  .step-content {
    padding: 20px 16px;
  }
  
  .templates-grid {
    grid-template-columns: 1fr;
  }
  
  .duration-options, .style-options {
    grid-template-columns: 1fr;
  }
  
  .step-actions {
    flex-direction: column;
  }
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.step-content {
  animation: fadeInUp 0.5s ease-out;
}

// 主题切换按钮样式
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.theme-toggle {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  i {
    font-size: 16px;
    color: white;
    transition: all 0.3s ease;
  }
}

// 暗色主题样式
.mobile-video-create.dark-theme {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  background-attachment: fixed; /* 确保背景固定 */
}

/* 为暗色主题确保背景色覆盖整个页面高度 */
.mobile-video-create.dark-theme::before {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
}

.mobile-video-create.dark-theme .mobile-header {
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h1 {
      color: #ffffff;
    }

    .quota-info {
      color: rgba(255, 255, 255, 0.8);
    }

    .back-btn, .videos-btn {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
      border: 1px solid rgba(255, 255, 255, 0.2);

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }

    .theme-toggle {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }

.mobile-video-create.dark-theme .main-content {
    background: transparent !important;

    .step-indicator {
      .step {
        background: rgba(15, 15, 35, 0.3) !important; // 使用与背景更相近的颜色，更低透明度
        border: none !important; // 移除边框
        color: rgba(255, 255, 255, 0.6);

        &.active {
          background: linear-gradient(135deg, #1a1a2e, #16213e) !important; // 使用与整体背景相近的渐变色
          color: #ffffff;
          border: none !important; // 移除边框
        }

        &.completed {
          background: linear-gradient(135deg, #1a1a2e, #16213e) !important; // 使用与整体背景相近的渐变色
          color: #ffffff;
          border: none !important; // 移除边框
        }
        
        .step-number {
          border: none !important; // 移除数字圆圈边框
          background: transparent !important; // 数字背景完全透明
        }
      }

      .step-connector {
        background: rgba(255, 255, 255, 0.1) !important; // 降低连接线的显著度

        &.active {
          background: linear-gradient(90deg, #1a1a2e, #16213e) !important; // 使用与背景相近的渐变色
        }
      }
    }

    .template-selection, .parameter-setting, .parameter-config, .video-generation, .generation-progress, .result-display {
      .step-content {
        background: rgba(30, 30, 50, 0.9) !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
      }

      .section-header {
        h2 {
          color: #ffffff !important;
        }

        p {
          color: rgba(255, 255, 255, 0.8) !important;
        }
      }

      .selected-template-info {
        background: rgba(45, 55, 72, 0.8) !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;

        h3 {
          color: #ffffff !important;
        }

        p {
          color: rgba(255, 255, 255, 0.8) !important;
        }
      }

      .step-actions {
        .prev-btn, .next-btn {
          background: linear-gradient(135deg, #2d3748, #4a5568) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #4a5568, #2d3748) !important;
            border: 1px solid rgba(255, 255, 255, 0.3) !important;
            transform: translateY(-2px);
          }

          &:disabled {
            background: rgba(30, 30, 50, 0.5) !important;
            color: rgba(255, 255, 255, 0.3) !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
          }
        }
      }

      .generation-preview {
        background: linear-gradient(135deg, rgba(26, 32, 44, 0.95) 0%, rgba(45, 55, 72, 0.95) 100%) !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;

        .preview-header {
          background: linear-gradient(135deg, #2d3748, #4a5568) !important;
          
          i, h4 {
            color: #e2e8f0 !important;
          }
        }
        
        .preview-content {
          background: transparent !important;
        }

        .preview-item {
          background: rgba(45, 55, 72, 0.6) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          
          &:hover {
            background: rgba(45, 55, 72, 0.8) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
          }
          
          &.template-item {
            background: linear-gradient(135deg, rgba(74, 144, 226, 0.15), rgba(139, 92, 246, 0.15)) !important;
            border: 1px solid rgba(74, 144, 226, 0.3) !important;
          }
          
          .item-icon {
            background: linear-gradient(135deg, #4a90e2, #8b5cf6) !important;
            
            i {
              color: #ffffff !important;
            }
          }
          
          .item-content {
            .label {
              color: rgba(255, 255, 255, 0.9) !important;
            }

            .value {
              color: #e2e8f0 !important;
            }
          }
        }
        
        // URL显示样式 - 暗色主题
        .url-display {
          background: rgba(30, 30, 50, 0.8) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          
          .url-text {
            color: #e2e8f0 !important;
          }
          
          .copy-url-btn {
            color: #4a90e2 !important;
            
            &:hover {
              background: #4a90e2 !important;
              color: #ffffff !important;
            }
          }
        }
        
        // 布尔值样式 - 暗色主题
        .bool-badge {
          &.bool-true {
            background: rgba(72, 187, 120, 0.2) !important;
            color: #68d391 !important;
            border: 1px solid rgba(72, 187, 120, 0.4) !important;
          }
          
          &.bool-false {
            background: rgba(245, 101, 101, 0.2) !important;
            color: #f56565 !important;
            border: 1px solid rgba(245, 101, 101, 0.4) !important;
          }
        }
        
        // 长文本样式 - 暗色主题
        .long-text {
          border-bottom-color: rgba(255, 255, 255, 0.3) !important;
        }
        
        // 图片预览样式 - 暗色主题
        .image-preview {
          background: rgba(30, 30, 50, 0.8) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          
          .preview-img {
            border-radius: 7px !important;
          }
          
          .image-actions {
            .copy-url-btn {
              background: rgba(0, 0, 0, 0.8) !important;
              
              &:hover {
                background: rgba(0, 0, 0, 0.95) !important;
              }
            }
            
            .open-image-btn {
              background: rgba(74, 144, 226, 0.8) !important;
              
              &:hover {
                background: rgba(74, 144, 226, 1) !important;
              }
            }
          }
          
          .image-error {
            background: rgba(60, 30, 30, 0.8) !important;
            color: #ff6b6b !important;
            
            i {
              color: #ff6b6b !important;
            }
          }
        }
      }

      .generation-status {
        .generation-ready {
          .ready-content {
            h3 {
              color: #ffffff !important;
            }
            
            p {
              color: rgba(255, 255, 255, 0.8) !important;
            }
          }

          .rocket-launch-container {
            .rocket-btn {
              background: linear-gradient(135deg, #4a90e2, #357abd) !important;
              box-shadow: 0 8px 25px rgba(74, 144, 226, 0.4) !important;

              &:hover:not(:disabled) {
                background: linear-gradient(135deg, #357abd, #2c5aa0) !important;
                box-shadow: 0 12px 35px rgba(74, 144, 226, 0.6) !important;
              }

              &.launching {
                &::before {
                  background: #90cdf4 !important;
                }

                &::after {
                  background: linear-gradient(90deg, transparent, #4a90e2, transparent) !important;
                }
              }
            }
          }

          .time-estimate {
            color: rgba(255, 255, 255, 0.8) !important;
          }
        }

        .generation-loading {
          .loading-icon {
            background: linear-gradient(135deg, #2d3748, #4a5568) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
          }

          h3, p {
            color: #ffffff !important;
          }
        }

        .generation-complete {
          .video-result {
            background: rgba(45, 55, 72, 0.8) !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
            padding: 24px 16px !important; // 保持与亮色主题相同的内边距

            h3 {
              color: #ffffff !important;
              margin-top: 0 !important; // 确保标题顶部不会太紧贴
              margin-bottom: 12px !important;
            }

            p {
              color: rgba(255, 255, 255, 0.8) !important;
              margin-bottom: 20px !important;
            }
            
            .video-actions {
              button {
                &.open-btn {
                  background: linear-gradient(135deg, #38a169, #2f855a) !important;
                  
                  &:hover {
                    background: linear-gradient(135deg, #2f855a, #276749) !important;
                  }
                }
                
                &.copy-btn {
                  background: linear-gradient(135deg, #3182ce, #2c5aa0) !important;
                  
                  &:hover {
                    background: linear-gradient(135deg, #2c5aa0, #2a5298) !important;
                  }
                }
                
                &.download-btn {
                  background: linear-gradient(135deg, #553c9a, #44337a) !important;
                  
                  &:hover:not(:disabled) {
                    background: linear-gradient(135deg, #44337a, #38296b) !important;
                  }
                  
                  &.downloading {
                    background: linear-gradient(135deg, #d69e2e, #b7791f) !important;
                    cursor: not-allowed;
                  }
                  
                  &:disabled {
                    opacity: 0.7 !important;
                    cursor: not-allowed;
                  }
                }
                
                &.share-btn {
                  background: linear-gradient(135deg, #c05621, #9c4221) !important;
                  
                  &:hover {
                    background: linear-gradient(135deg, #9c4221, #7c351a) !important;
                  }
                }
                
                &.reload-btn {
                  background: linear-gradient(135deg, #d39e00, #b7870c) !important;
                  color: #ffffff !important;
                  
                  &:hover {
                    background: linear-gradient(135deg, #b7870c, #975a16) !important;
                  }
                  
                  &.full-width {
                    grid-column: 1 / -1; // 跨越所有列
                  }
                }
              }
            }
            
            .video-error {
              background: rgba(60, 30, 30, 0.8) !important;
              border: 1px solid rgba(255, 76, 76, 0.3) !important;
              
              i {
                color: #ff6b6b !important;
              }
              
              p {
                color: rgba(255, 255, 255, 0.9) !important;
              }
            }
          }
        }
      }
    }

    // 分类标签暗色主题
    .category-tabs {
      .category-tab {
        background: rgba(30, 30, 50, 0.8) !important;
        color: rgba(255, 255, 255, 0.8) !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;

        &:hover {
          background: rgba(45, 55, 72, 0.9) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
        }

        &.active {
          background: linear-gradient(135deg, #2d3748, #4a5568) !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
        }
      }
    }

    // 模版卡片暗色主题
    .template-card {
      background: rgba(30, 30, 50, 0.8) !important;
      border: 2px solid rgba(255, 255, 255, 0.1) !important;

      &:hover {
        background: rgba(45, 55, 72, 0.9) !important;
        border: 2px solid rgba(255, 255, 255, 0.2) !important;
        transform: translateY(-2px);
      }

      &.selected {
        background: rgba(45, 55, 72, 0.9) !important;
        border-color: #4a90e2 !important;
        box-shadow: 0 8px 20px rgba(74, 144, 226, 0.3) !important;
      }

      .template-info {
        h4 {
          color: #ffffff !important;
        }

        p {
          color: rgba(255, 255, 255, 0.8) !important;
        }
      }
    }

    // 搜索框暗色主题
    .search-bar {
      background: rgba(30, 30, 50, 0.8) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
      border-radius: 24px !important;

      i {
        color: rgba(255, 255, 255, 0.6) !important;
      }

      input {
        background: transparent !important;
        color: #ffffff !important;
        border: none !important;

        &::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
        }

        &:focus {
          background: transparent !important;
          outline: none !important;
        }
      }

      &:focus-within {
        background: rgba(30, 30, 50, 1) !important;
        border-color: #4a90e2 !important;
        box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
      }
    }

    // 模版网格容器暗色主题
    .templates-grid {
      background: transparent !important;
    }

    // 整体步骤内容容器强制暗色背景
    .step-content {
      background: rgba(30, 30, 50, 0.9) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
    }

    // 进度条和生成状态暗色主题
    .generation-progress {
      .progress-icon {
        background: linear-gradient(135deg, #2d3748, #4a5568) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
      }

      .progress-bar {
        background: rgba(45, 55, 72, 0.8) !important;

        .progress-fill {
          background: linear-gradient(135deg, #4a90e2, #357abd) !important;
        }
      }

      h3, p {
        color: #ffffff !important;
      }

      .simple-time-info {
        .time-item span {
          background: rgba(255, 255, 255, 0.05) !important;
          color: rgba(255, 255, 255, 0.8) !important;
        }
      }
    }

    // 参数配置区域暗色主题  
    .parameter-config, .parameter-form {
      .param-group {
        .param-label, label {
          color: #ffffff !important;
        }

        .form-input, .form-textarea, .form-select,
        input, textarea, select {
          background: rgba(30, 30, 50, 0.8) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;

          &:focus {
            background: rgba(30, 30, 50, 1) !important;
            border-color: #4a90e2 !important;
            box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.2) !important;
          }

          &::placeholder {
            color: rgba(255, 255, 255, 0.6) !important;
          }
          
          &[readonly] {
            background: rgba(20, 20, 35, 0.6) !important;
            color: rgba(255, 255, 255, 0.4) !important;
            cursor: not-allowed;
            border-color: rgba(255, 255, 255, 0.1) !important;
            
            &:focus {
              border-color: rgba(255, 255, 255, 0.1) !important;
              box-shadow: none !important;
            }
            
            &::placeholder {
              color: rgba(255, 255, 255, 0.3) !important;
            }
          }
        }

        .checkbox-label {
          .checkbox-text {
            color: rgba(255, 255, 255, 0.8) !important;
          }
        }

        // 暗色主题下的选项按钮组
        .option-buttons {
          .option-btn {
            background: rgba(45, 55, 72, 0.8) !important;
            border: 2px solid rgba(255, 255, 255, 0.2) !important;
            color: rgba(255, 255, 255, 0.9) !important;

            &:hover {
              border-color: #4a90e2 !important;
              background: rgba(74, 144, 226, 0.2) !important;
              transform: translateY(-1px);
              box-shadow: 0 4px 8px rgba(74, 144, 226, 0.3) !important;
            }

            &.active {
              border-color: #4a90e2 !important;
              background: linear-gradient(135deg, #4a90e2, #357abd) !important;
              color: white !important;
              box-shadow: 0 4px 12px rgba(74, 144, 226, 0.5) !important;

              &:hover {
                background: linear-gradient(135deg, #357abd, #2c5aa0) !important;
                transform: translateY(-2px);
                box-shadow: 0 6px 16px rgba(74, 144, 226, 0.6) !important;
              }
            }
          }
        }

        .url-input-group {
          .upload-btn {
            background: linear-gradient(135deg, #2d3748, #4a5568) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;

            &:hover {
              background: linear-gradient(135deg, #4a5568, #2d3748) !important;
              border: 1px solid rgba(255, 255, 255, 0.3) !important;
            }
          }
        }

        .param-description {
          color: rgba(255, 255, 255, 0.7) !important;
        }

        .file-preview {
          .preview-item {
            background: rgba(30, 30, 50, 0.8) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
            
            &:hover {
              box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4) !important;
            }
            
            .preview-header {
              background: rgba(45, 55, 72, 0.8) !important;
              border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
              
              .preview-status {
                .status-badge {
                  &.status-uploaded {
                    background: rgba(34, 197, 94, 0.2) !important;
                    color: #86efac !important;
                    border: 1px solid rgba(34, 197, 94, 0.4) !important;
                    
                    i {
                      color: #22c55e !important;
                    }
                  }
                  
                  &.status-local {
                    background: rgba(245, 158, 11, 0.2) !important;
                    color: #fbbf24 !important;
                    border: 1px solid rgba(245, 158, 11, 0.4) !important;
                    
                    i {
                      color: #f59e0b !important;
                    }
                  }
                }
              }
              
              .remove-btn {
                background: rgba(239, 68, 68, 0.2) !important;
                color: #f87171 !important;
                border: 1px solid rgba(239, 68, 68, 0.4) !important;
                
                &:hover {
                  background: rgba(239, 68, 68, 0.3) !important;
                  color: #ef4444 !important;
                }
              }
            }
            
            .preview-content {
              background: transparent !important;
            }
            
            .preview-footer {
              background: rgba(45, 55, 72, 0.8) !important;
              border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
              
              .file-info {
                color: rgba(255, 255, 255, 0.8) !important;
                
                i {
                  color: #a78bfa !important;
                }
                
                .file-type {
                  color: rgba(255, 255, 255, 0.9) !important;
                }
              }
            }
          }
        }
      }

      .option-item {
        background: rgba(30, 30, 50, 0.8) !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;

        &:hover {
          background: rgba(45, 55, 72, 0.9) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
        }

        &.selected {
          background: rgba(45, 55, 72, 0.9) !important;
          border-color: #4a90e2 !important;
        }
      }
    }

    // 无参数提示暗色主题
    .no-params-tip {
      background: rgba(30, 30, 50, 0.8) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;

      i {
        color: #4a90e2 !important;
      }

      p {
        color: rgba(255, 255, 255, 0.8) !important;
      }
    }

    // 结果展示区域暗色主题
    .result-display {
      .video-preview {
        background: rgba(30, 30, 50, 0.8) !important;
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
      }

      .video-info {
        h4, p {
          color: #ffffff !important;
        }
      }
    }

    // 按钮样式
    .prev-btn, .next-btn, .generate-btn, .download-btn, .share-btn, .create-new-btn, .open-btn, .reload-btn, .copy-btn {
      background: linear-gradient(135deg, #2d3748, #4a5568);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #e2e8f0;
      font-weight: 500;
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #4a5568, #2d3748);
        border: 1px solid rgba(255, 255, 255, 0.3);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }

      &:disabled {
        background: rgba(30, 30, 50, 0.5);
        color: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.1);
        cursor: not-allowed;
        transform: none;
      }
    }

    // 覆盖所有文字颜色为白色
    h1, h2, h3, h4, h5, h6,
    p, span, div, li, label,
    .step-title, .step-description,
    .template-card h4, .template-card p,
    .category-tab, .param-group label,
    .form-input, .form-textarea,
    .option-item, .video-info h4, .video-info p,
    .progress-info, .modal-body p {
      color: #ffffff !important;
    }

    // 特殊颜色保持不变
    .status {
      &.completed { color: #68d391 !important; }
      &.processing { color: #f6e05e !important; }
      &.failed { color: #fc8181 !important; }
    }
  }

// 全局视频结果显示区域
.global-video-result {
  padding: 16px;
  background: rgba(255, 255, 255, 0.95);
  margin: 16px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  .video-result-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      h3 {
        margin: 0;
        color: #2c3e50;
        font-size: 18px;
        display: flex;
        align-items: center;
        gap: 8px;
        
        i {
          color: #667eea;
        }
      }
      
      .back-to-video-btn {
        padding: 6px 12px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;
        transition: all 0.3s ease;
        
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }
      }
    }
    
    .video-preview {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      
      .preview-video {
        width: 120px;
        height: 80px;
        border-radius: 8px;
        object-fit: cover;
        flex-shrink: 0;
      }
      
      .video-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 80px;
        
        .video-title {
          margin: 0;
          color: #2c3e50;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.3;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        
        .video-actions-mini {
          display: flex;
          gap: 6px;
          
          .mini-btn {
            width: 28px;
            height: 28px;
            border: none;
            border-radius: 6px;
            background: #f8f9fa;
            color: #666;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            
            &:hover:not(:disabled) {
              background: #e9ecef;
              color: #333;
            }
            
            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
              background: #f1f1f1;
              color: #999;
            }
          }
        }
      }
    }
  }
}

// 暗色主题下的全局视频结果和模板预览弹窗
.mobile-video-create.dark-theme {
  .global-video-result {
    background: rgba(30, 30, 50, 0.9) !important;
    
    .video-result-card {
      .card-header {
        h3 {
          color: #ffffff !important;
          
          i {
            color: #4a90e2 !important;
          }
        }
        
        .back-to-video-btn {
          background: linear-gradient(135deg, #2d3748, #4a5568) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          
          &:hover {
            background: linear-gradient(135deg, #4a5568, #2d3748) !important;
            border: 1px solid rgba(255, 255, 255, 0.3) !important;
          }
        }
      }
      
      .video-preview {
        .video-info {
          .video-title {
            color: #ffffff !important;
          }
          
          .video-actions-mini {
            .mini-btn {
              background: rgba(45, 55, 72, 0.8) !important;
              color: rgba(255, 255, 255, 0.8) !important;
              border: 1px solid rgba(255, 255, 255, 0.1) !important;
              
              &:hover:not(:disabled) {
                background: rgba(74, 85, 104, 0.9) !important;
                color: #ffffff !important;
                border: 1px solid rgba(255, 255, 255, 0.2) !important;
              }
              
              &:disabled {
                opacity: 0.5 !important;
                cursor: not-allowed;
                background: rgba(30, 30, 50, 0.6) !important;
                color: rgba(255, 255, 255, 0.4) !important;
              }
            }
          }
        }
      }
    }
  }

  // 模板预览弹窗暗色主题
  .template-preview-modal {
    background: rgba(30, 30, 50, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    
    .modal-header {
      background: rgba(45, 55, 72, 0.8) !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
      
      h3 {
        color: #ffffff !important;
      }
      
      .close-btn {
        background: rgba(255, 255, 255, 0.1) !important;
        color: rgba(255, 255, 255, 0.8) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        
        &:hover {
          background: rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
        }
      }
    }
    
    .template-preview-body {
      .template-info {
        .template-description, .template-tags, .template-params-info, .template-duration {
          h4 {
            color: #ffffff !important;
          }
          
          p {
            color: rgba(255, 255, 255, 0.8) !important;
          }
        }
        
        .template-tags {
          .tags-list {
            .tag {
              background: rgba(45, 55, 72, 0.8) !important;
              color: rgba(255, 255, 255, 0.9) !important;
            }
          }
        }
        
        .template-params-info {
          .params-preview {
            .param-item, .more-params {
              color: rgba(255, 255, 255, 0.8) !important;
              
              i {
                color: rgba(255, 255, 255, 0.6) !important;
              }
              
              .required-mark {
                color: #ff6b6b !important;
              }
            }
            
            .more-params {
              color: rgba(255, 255, 255, 0.6) !important;
            }
          }
        }
        
        .template-duration {
          p {
            color: rgba(255, 255, 255, 0.8) !important;
            
            i {
              color: rgba(255, 255, 255, 0.6) !important;
            }
          }
        }
      }
    }
    
    .template-preview-footer {
      background: rgba(45, 55, 72, 0.8) !important;
      border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
      
      .cancel-btn {
        background: rgba(60, 70, 90, 0.8) !important;
        color: rgba(255, 255, 255, 0.8) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        
        &:hover {
          background: rgba(74, 85, 104, 0.9) !important;
          color: #ffffff !important;
        }
      }
      
      .confirm-btn {
        background: linear-gradient(135deg, #2d3748, #4a5568) !important;
        color: #ffffff !important;
        border: 1px solid rgba(74, 144, 226, 0.3) !important;
        
        &:hover {
          background: linear-gradient(135deg, #4a5568, #2d3748) !important;
          box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4) !important;
        }
      }
    }
  }
}

</style>
